import {
  type Job,
  type RestoreResult,
  type SoftDeleteResult,
  type TrashListResult,
  type TrashPurgeRequestInput,
  jobSchema,
  restoreResultSchema,
  softDeleteResultSchema,
  trashListResultSchema,
} from '@xphoto/shared';
import { ApiError } from '../gallery/api';
import { apiFetch } from '../lib/http';

/**
 * Client typé de la CORBEILLE (soft-delete réversible, Lot 1). Comme les autres
 * clients, la réponse est RE-PARSÉE via son schéma partagé `@xphoto/shared`
 * (« never trust the client », y compris ses propres réponses).
 *
 * `restoreAssets`/`fetchTrash` seront consommés par l'écran de gestion de la corbeille
 * (Lot 2) ; ils sont exportés dès maintenant (couverts par tests) pour ne pas être du
 * code mort et fournir l'API stable dont le Lot 2 dépendra.
 */

async function readJson(res: Response): Promise<unknown> {
  if (!res.ok) {
    let code = 'UNKNOWN';
    try {
      const body = (await res.json()) as { error?: { code?: string } };
      if (typeof body?.error?.code === 'string') code = body.error.code;
    } catch {
      // Corps non-JSON : on garde le code générique.
    }
    throw new ApiError(res.status, code);
  }
  return res.json();
}

/**
 * `POST /api/libraries/:id/assets/soft-delete` — met à la corbeille une sélection
 * d'assets (suppression LOGIQUE réversible, aucun fichier touché). Renvoie
 * `{ deleted }` (nombre RÉELLEMENT supprimés). 403 (grantee lecture) → `ApiError`.
 */
export async function softDeleteAssets(
  libraryId: string,
  assetIds: readonly string[],
): Promise<SoftDeleteResult> {
  const res = await apiFetch(`/api/libraries/${libraryId}/assets/soft-delete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ assetIds }),
  });
  return softDeleteResultSchema.parse(await readJson(res));
}

/**
 * `POST /api/libraries/:id/assets/restore` — restaure (sort de la corbeille) une
 * sélection d'assets. Renvoie `{ restored }`. Consommé par le Lot 2.
 */
export async function restoreAssets(
  libraryId: string,
  assetIds: readonly string[],
): Promise<RestoreResult> {
  const res = await apiFetch(`/api/libraries/${libraryId}/assets/restore`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ assetIds }),
  });
  return restoreResultSchema.parse(await readJson(res));
}

/**
 * `GET /api/libraries/:id/trash?limit=&offset=` — page d'assets en corbeille +
 * total. Consommé par l'écran de gestion de la corbeille (Lot 2).
 */
export async function fetchTrash(
  libraryId: string,
  limit: number,
  offset: number,
): Promise<TrashListResult> {
  const sp = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  const res = await apiFetch(`/api/libraries/${libraryId}/trash?${sp.toString()}`);
  return trashListResultSchema.parse(await readJson(res));
}

/**
 * `POST /api/libraries/:id/trash/purge` — PURGE DÉFINITIVEMENT (irréversible) une
 * sélection (`assetIds`) OU toute la corbeille (`all: true`). ÉCRITURE DISQUE : `unlink`
 * du fichier PUIS suppression DB. S'exécute en JOB asynchrone → renvoie le `Job` (à
 * suivre dans la cloche). 409 `WRITE_DISABLED` (bibliothèque en lecture seule) → `ApiError`.
 */
export async function purgeTrash(libraryId: string, body: TrashPurgeRequestInput): Promise<Job> {
  const res = await apiFetch(`/api/libraries/${libraryId}/trash/purge`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return jobSchema.parse(await readJson(res));
}
